import { Component } from '@angular/core';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { GenericCrudAddComponent } from 'src/app/libs/components/generic-crud/generic-crud-add.component';
import { ProductBatchTechnicalFeature, TId, TechnicalFeature, TechnicalFeaturesGroups } from 'src/app/models';

@Component({
    selector: 'app-productbatch-technicalfeature-add',
    templateUrl: './productbatch-technicalfeature-add.component.html',
    styleUrl: './productbatch-technicalfeature-add.component.sass',
    standalone: false
})
export class ProductbatchTechnicalfeatureAddComponent extends GenericCrudAddComponent<ProductBatchTechnicalFeature> {

  productBatchId:TId = '';
  dataSource:TechnicalFeature[] | TechnicalFeaturesGroups[] = [];

  optionTechnicalFeature:boolean = false;
  optionTechnicalFeatureGroup:boolean = false;
  lookupLabel:string = '';

  technicalFeaturesSelected:TId[] = [];
  technicalFeaturesAlreadyIncluded:TId[] = [];

  groupsStore: {[key:TId]:TId[]} = {};

  constructor(private translation:TranslateService) {
    super();
  }

  protected override async preFetch(): Promise<void> {
    this.productBatchId = this.initialFormValues.productBatchId;
    this.optionTechnicalFeature = this.initialFormValues.addOption === 'addTechnicalfeature';
    this.optionTechnicalFeatureGroup = this.initialFormValues.addOption === 'addTechnicalfeatureGroup';

    this.resource = this.api.resources.product.productBatchTechnicalFeatures(this.productBatchId);
    this.technicalFeaturesAlreadyIncluded = await this.getTechnicalFeaturesAlredyIncludedInBatch(this.productBatchId);

    if (this.optionTechnicalFeature) {
      this.lookupLabel = 'technicalFeatures';
      this.dataSource = await firstValueFrom(this.api.resources.product.technicalfeatures.getAll({ companyId: this.api.auth.userValue?.lastCompanyId }));
    }

    if (this.optionTechnicalFeatureGroup) {
      this.lookupLabel = 'technicalFeaturesGroups';
      this.dataSource = await firstValueFrom(this.api.resources.product.technicalFeatureGroups.getAll({ companyId: this.api.auth.userValue?.lastCompanyId }));
      this.storeGroupsAndTechnicalFeaturesRelations(this.dataSource);
    }

    this.lookupLabel = this.translation.instant(`menu.breadcrumb.${this.lookupLabel}`);
  }

  private async storeGroupsAndTechnicalFeaturesRelations(groups:TechnicalFeaturesGroups[]) {
    this.groupsStore = {};

    for (const group of groups) {
      const features = await firstValueFrom(this.api.resources.product.technicalFeaturesOfGroup(group.id).getAll());
      this.groupsStore[group.id] = features.map(_ => _.id);
    }
  }

  private getTechnicalFeaturesAlredyIncludedInBatch = async (batchId:TId) : Promise<TId[]> => {
    const tfs = await firstValueFrom(this.api.resources.product.productBatchTechnicalFeatures(this.productBatchId).getAll());
    return tfs.map(tf => tf.technicalFeatureId);
  }

  override initFormGroup(): FormGroup<any> {
    return this.fb.group({
      technicalFeatureId: [undefined, [this.technicalFeatureAlreadyIncluded(), this.allTechnicalFeaturesInGroupAlreadyIncluded()]],
    });
  }

  async onValueChange(event:any) {
    this.technicalFeaturesSelected = [];

    if (this.optionTechnicalFeature)
      this.technicalFeaturesSelected.push(event);

    if (this.optionTechnicalFeatureGroup)
      this.groupsStore[event].forEach(id => this.technicalFeaturesSelected.push(id));
  }

  override async createEntity(): Promise<void> {
    if (!await this.validateEntity())
      return;

    this.technicalFeaturesSelected.forEach(async tf => {
      const result = await firstValueFrom(this.resource!.post({
        productBatchId: this.productBatchId,
        technicalFeatureId: tf,
        value: '',
      }));

      this.entityCreated.emit(result);
    })
  }

  private technicalFeatureAlreadyIncluded():ValidatorFn {
    return (control:AbstractControl):{[key:string]:boolean} | null => {
      if (this.optionTechnicalFeature && control.value)
        if (this.technicalFeaturesAlreadyIncluded.some(_ => _ === control.value))
          return {'technicalFeatureIsAlreadyInThisFamily': true};
      return null;
    }
  }

  private allTechnicalFeaturesInGroupAlreadyIncluded():ValidatorFn {
    return (control:AbstractControl):{[key:string]:boolean} | null => {
      if (this.optionTechnicalFeatureGroup && control.value)
        if (this.groupsStore[control.value].every(id => this.technicalFeaturesAlreadyIncluded.includes(id)))
          return { 'allTechnicalFeaturesAreAlreadyInThisFamily': true };
      return null;
    }
  }
}
